import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import {EditPermissions} from '../../../../../../../../../shared/models';
import {required} from '../../../../../../../../../shared/utils';
import { DaterangepickerDirective, NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import moment from 'moment';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { InputValidatorsComponent } from '../../../../templates/input-validators/input-validators.component';

interface DatePickerPage {
  pageType: 'datePickerPages';
  order?: number;
  pageHeader: string;
  pageSubHeader: string;
  hintText: string;
  labelText: string;
  dateFormat: string;
  defaultDate: string;
  endDate: string;
  startDate: string;
}

@Component({
    selector: 'datepicker-page',
    templateUrl: './datepicker-page.component.html',
    styleUrls: ['../styles.scss', './datepicker-page.component.scss'],
    imports: [NgTemplateOutlet, NgClass, ReactiveFormsModule, InputValidatorsComponent, NgxDaterangepickerMd]
})

export class DatepickerPageComponent implements OnInit, OnDestroy {
  constructor() {}

  @Input() pageData: any;
  @Input() canEdit: EditPermissions;
  @Output() setPageData = new EventEmitter();
  @ViewChildren(DaterangepickerDirective) picks!: QueryList<DaterangepickerDirective>;

  moreInfoOpened = false;

  datePickerPageForm: UntypedFormGroup;
  dateRange: null;
  defaultDate: null;

  ngOnInit() {
    this.datePickerPageForm = this.getDatePickerPageTemplate();

    const { page, newPage } = this.pageData;
    this.datePickerPageForm.patchValue(page);

    if (newPage === false) {
      this.datePickerPageForm.markAllAsTouched();
    }

    this.setPageDataHandler();
    this.listenForFormChanges();
  }

  ngOnDestroy() {
    this.setPageDataHandler();
  }

  getDatePickerPageTemplate() {
    return new UntypedFormGroup({
      pageHeader: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      pageSubHeader: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }, [required('Subheader')]),
      hintText: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      labelText: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      dateFormat: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      defaultDate: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      endDate: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      startDate: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      })
    });
  }

  setPageDataHandler() {
    const data = {
      ...this.pageData,
      page: this.getData()
    };
    this.setPageData.emit(data);
  }

  listenForFormChanges() {
    this.datePickerPageForm.valueChanges.subscribe(values => {
      this.setPageDataHandler();
    });
  }

  getData() {
    const pageFormValues = this.datePickerPageForm.getRawValue();
    return {
      ...pageFormValues,
      pageType: this.pageData.type,
      valid: this.datePickerPageForm.valid
    };
  }

  openMoreInfo() {
    this.moreInfoOpened = true;
  }

  closeMoreInfo() {
    this.moreInfoOpened = false;
  }

  resetDates() {
    if (this.defaultDate) {
      this.setDefaultDateValue(this.defaultDate);
    }
    if (this.dateRange) {
      this.setRangeDataValues(this.dateRange);
    }
  }

  datesUpdated(event, type) {
    if (type === 'defaultDate') {
      this.defaultDate = event;
      this.setDefaultDateValue(event);
    }
    if (type === 'dateRange') {
      this.dateRange = event;
      this.setRangeDataValues(event);
    }
  }

  setRangeDataValues(event) {
    const form = this.datePickerPageForm;
    const startDate = moment(event.startDate.$d).format('MM/DD/YYYY');
    const endDate = moment(event.endDate.$d).format('MM/DD/YYYY');
    form.get('startDate').setValue(startDate);
    form.get('endDate').setValue(endDate);
  }

  setDefaultDateValue(event) {
    const form = this.datePickerPageForm;
    const startDate = moment(event.startDate.$d).format('MM/DD/YYYY');
    form.get('defaultDate').setValue(startDate);
  }

  openDatepicker(event, index) {
    if (this.picks && this.picks['_results'] && this.picks['_results'][index]) {
      this.picks['_results'][index].open(event);
    }
  }
}
